import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PawPrint, LockKey, Trophy, Ticket, Copy, CreditCard, ShieldCheck, WarningCircle } from "@phosphor-icons/react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PaymentDisclaimer from "@/components/PaymentDisclaimer";
import LegalNotice from "@/components/LegalNotice";
import TrialCountdown from "@/components/TrialCountdown";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const TIER_LABELS = {
  1: { label: "Tier 1", cls: "h57-chip", desc: "Starter — 25 listings, $50 max list price." },
  2: { label: "Tier 2", cls: "h57-chip-orange", desc: "Established — no listing count / price cap." },
  3: { label: "Tier 3", cls: "h57-chip-gold", desc: "Trusted — no limits + can generate referral vouchers." },
};

export default function Account() {
  const { user, refresh } = useAuth();
  const [params] = useSearchParams();
  const isWelcome = params.get("welcome") === "1";
  const shouldFocusUpgrade = params.get("upgrade") === "1";
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [vendorCode, setVendorCode] = useState("");
  const [upgrading, setUpgrading] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const [application, setApplication] = useState({ reason: "", experience: "", social: "" });
  const [applying, setApplying] = useState(false);
  const [acceptVendorTerms, setAcceptVendorTerms] = useState(false);
  const [tierStatus, setTierStatus] = useState(null);
  const [standing, setStanding] = useState(null);
  const [billing, setBilling] = useState(null);
  const [ccBusy, setCcBusy] = useState(false);
  const [vouchers, setVouchers] = useState([]);
  const [creatingVoucher, setCreatingVoucher] = useState(false);
  // Confirmation modal shown when a vendor toggles "Accept credit card payments" ON.
  // Reminds them Hunt57 does NOT process CC — they must have a way to generate pay links.
  const [ccConfirmOpen, setCcConfirmOpen] = useState(false);
  const [newVoucher, setNewVoucher] = useState({ max_uses: 1, note: "" });

  useEffect(() => {
    if (user) {
      setForm({
        display_name: user.display_name || "",
        bio: user.bio || "",
        state: user.state || "",
        ships_from_state: user.ships_from_state || "",
        typical_ship_time: user.typical_ship_time || "",
        shipping_policy: user.shipping_policy || "",
        return_policy: user.return_policy || "",
        paypal_link: user.paypal_link || "",
        cashapp_handle: user.cashapp_handle || "",
        venmo_link: user.venmo_link || "",
        other_payment_notes: user.other_payment_notes || "",
        accepts_credit_card: !!user.accepts_credit_card,
        credit_card_note: user.credit_card_note || "",
        combined_shipping_enabled: !!user.combined_shipping_enabled,
        combined_shipping_first: user.combined_shipping_first ?? "",
        combined_shipping_addl: user.combined_shipping_addl ?? "",
        combined_shipping_max: user.combined_shipping_max ?? "",
        handling_time_days: user.handling_time_days ?? 3,
        shipping_name: user.shipping_name || "",
        shipping_line1: user.shipping_line1 || "",
        shipping_line2: user.shipping_line2 || "",
        shipping_city: user.shipping_city || "",
        shipping_state: user.shipping_state || "",
        shipping_zip: user.shipping_zip || "",
        shipping_country: user.shipping_country || "",
        shipping_phone: user.shipping_phone || "",
      });
    }
  }, [user]);

  const isVendor = user?.is_vendor || user?.role === "admin";

  const loadTierStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/vendor/tier-status");
      setTierStatus(data);
    } catch { /* silent */ }
  }, []);

  const loadStanding = useCallback(async () => {
    try {
      const { data } = await api.get("/vendor/standing");
      setStanding(data);
    } catch { /* silent */ }
  }, []);

  const loadBilling = useCallback(async () => {
    try {
      const { data } = await api.get("/billing/config");
      setBilling(data);
    } catch { /* silent */ }
  }, []);

  const startCardSetup = async () => {
    setCcBusy(true);
    try {
      const { data } = await api.post("/billing/setup-session", { origin_url: window.location.origin });
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (err) { toast.error(formatErr(err)); setCcBusy(false); }
  };

  const openBillingPortal = async () => {
    setCcBusy(true);
    try {
      const { data } = await api.post("/billing/portal-session");
      if (data.portal_url) window.location.href = data.portal_url;
    } catch (err) { toast.error(formatErr(err)); setCcBusy(false); }
  };

  const loadVouchers = useCallback(async () => {
    if (!isVendor) return;
    try {
      const { data } = await api.get("/vouchers/mine");
      setVouchers(data.vouchers || []);
    } catch { /* silent — non-Tier3 users get 403 which is fine */ }
  }, [isVendor]);

  useEffect(() => {
    if (user) { loadTierStatus(); loadVouchers(); loadBilling(); loadStanding(); }
  }, [user, loadTierStatus, loadVouchers, loadBilling, loadStanding]);

  // Handle the return from Stripe Checkout (setup mode). In production the
  // checkout.session.completed webhook writes stripe_payment_method_id on
  // the user doc, but in preview/local (no webhook tunnel) it never fires —
  // so we ALSO call /billing/sync-session with the session_id to force-sync
  // the payment method from Stripe. Then refresh billing + user state (with
  // retries) so Option 2 (voucher redemption) unlocks immediately.
  useEffect(() => {
    const billingRet = params.get("billing");
    if (billingRet === "setup_complete") {
      const sessionId = params.get("session_id");
      const finalize = async () => {
        if (sessionId) {
          try { await api.post("/billing/sync-session", { session_id: sessionId }); }
          catch { /* webhook may have already run — non-fatal */ }
        }
        await Promise.all([loadBilling(), refresh()]);
      };
      toast.success("Card saved — you can now apply or redeem a referral code.");
      finalize();
      setTimeout(finalize, 2000);
      setTimeout(finalize, 5000);
    } else if (billingRet === "cancelled") {
      toast.error("Card setup cancelled. You can retry any time.");
    }
  }, [params, loadBilling, refresh]);

  // Auto-scroll to the vendor upgrade card if the user came from /sell.
  useEffect(() => {
    if (shouldFocusUpgrade) {
      const el = document.querySelector('[data-testid="vendor-upgrade-card"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [shouldFocusUpgrade, user]);

  const save = async (e) => {
    e.preventDefault();
    // Normalize the combined-shipping fields — empty strings become null and
    // numeric strings become numbers so the backend validators are happy.
    const payload = { ...form };
    if (isVendor) {
      const asNum = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
      payload.combined_shipping_enabled = !!form.combined_shipping_enabled;
      payload.combined_shipping_first = asNum(form.combined_shipping_first);
      payload.combined_shipping_addl = asNum(form.combined_shipping_addl);
      payload.combined_shipping_max = asNum(form.combined_shipping_max);
      const handling = Number(form.handling_time_days);
      if (!Number.isInteger(handling) || handling < 1 || handling > 7) {
        toast.error("Order handling time must be 1-7 days.");
        return;
      }
      payload.handling_time_days = handling;
      if (payload.combined_shipping_enabled) {
        if (payload.combined_shipping_first == null || payload.combined_shipping_addl == null || payload.combined_shipping_max == null) {
          toast.error("Enter the initial charge, additional-per-card, and max-per-package to enable combined shipping.");
          return;
        }
        if (payload.combined_shipping_max < 2) {
          toast.error("Max cards per package must be at least 2.");
          return;
        }
      }
    }
    setSaving(true);
    try {
      await api.patch("/users/me", payload);
      await refresh();
      toast.success("Profile saved");
    } catch (err) { toast.error(formatErr(err)); }
    finally { setSaving(false); }
  };

  const upgradeToVendor = async (e) => {
    e.preventDefault();
    const code = vendorCode.trim().toUpperCase();
    if (!code) { toast.error("Enter a referral code"); return; }
    if (!acceptVendorTerms) { toast.error("Please read and accept the Hunt57 Vendor Terms."); return; }
    setUpgrading(true);
    try {
      await api.post("/users/me/upgrade-to-vendor", { code, accept_vendor_terms: true });
      await refresh();
      await loadTierStatus();
      toast.success("You're a vendor now. Add your payment paths, then list your first card.");
      setVendorCode("");
      setAcceptVendorTerms(false);
    } catch (err) { toast.error(formatErr(err)); }
    finally { setUpgrading(false); }
  };

  const submitApplication = async () => {
    if (!acceptVendorTerms) { toast.error("Please read and accept the Hunt57 Vendor Terms."); return; }
    setApplying(true);
    try {
      await api.post("/users/me/apply-vendor", { ...application, accept_vendor_terms: true });
      await refresh();
      toast.success("Application submitted — an admin will review it shortly.");
      setApplyOpen(false);
      setApplication({ reason: "", experience: "", social: "" });
      setAcceptVendorTerms(false);
    } catch (err) { toast.error(formatErr(err)); }
    finally { setApplying(false); }
  };

  const createVoucher = async () => {
    setCreatingVoucher(true);
    try {
      const { data } = await api.post("/vouchers", {
        max_uses: Number(newVoucher.max_uses) || 1,
        note: newVoucher.note || null,
      });
      toast.success(`Voucher ${data.voucher.code} created`);
      setNewVoucher({ max_uses: 1, note: "" });
      loadVouchers();
    } catch (err) { toast.error(formatErr(err)); }
    finally { setCreatingVoucher(false); }
  };

  const revokeVoucher = async (id, code) => {
    if (!window.confirm(`Revoke voucher ${code}? It won't be usable anymore.`)) return;
    try {
      await api.post(`/vouchers/${id}/revoke`);
      toast.success("Voucher revoked");
      loadVouchers();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success(`Copied ${text}`); }
    catch { toast.error("Copy failed"); }
  };

  if (!user) return null;

  // Hold tier-dependent UI until /vendor/tier-status resolves so we don't
  // flicker "Tier 1" defaults for admins / Tier-2 / Tier-3 vendors.
  const tier = tierStatus?.tier;
  const tierMeta = tier ? TIER_LABELS[tier] : null;
  const applicationPending = user.vendor_status === "pending";
  const applicationRejected = user.vendor_status === "rejected";
  const canCreateVouchers = tier === 3;

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-4 md:py-8">
      <div className="font-mono text-xs text-[#FF5F1F] tracking-widest uppercase">→ Account</div>
      <h1 className="mt-2 font-display font-black uppercase text-3xl md:text-4xl tracking-tighter">@{user.username}</h1>
      <div className="mt-2 flex flex-wrap gap-2">
        {user.role === "admin" && <span className="h57-chip h57-chip-gold">Admin</span>}
        {isVendor ? (
          tier ? (
            <span className={`h57-chip ${tierMeta?.cls || ""}`} data-testid="account-tier-chip">Vendor · {tierMeta?.label || `Tier ${tier}`}</span>
          ) : (
            <span className="h57-chip opacity-50" data-testid="account-tier-chip">Vendor · Loading…</span>
          )
        ) : (
          <span className="h57-chip">Member</span>
        )}
        {isVendor && user.membership_status && user.membership_status !== "none" && (
          <span className="h57-chip">{user.membership_status}</span>
        )}
        {user.founding_member && <span className="h57-chip h57-chip-gold">Founding Member</span>}
      </div>

      {isWelcome && (
        <div className="mt-6 h57-surface p-5 border-[#FF5F1F]/40">
          <div className="font-mono text-xs uppercase tracking-widest text-[#FF5F1F]">→ Welcome to Hunt57</div>
          <p className="mt-2 text-zinc-300">
            Your account is ready. Browse the marketplace freely.
            When you&apos;re ready to sell, upgrade to a vendor account below.
          </p>
        </div>
      )}

      {/* Account Standing — 3 checks visualization */}
      {standing && (
        <section className="mt-6 h57-surface p-5" data-testid="account-standing-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF5F1F]">→ Account Standing</div>
              <div className="mt-1 font-display font-black uppercase text-xl tracking-tight" data-testid="standing-label">
                {standing.label}
              </div>
              <div className="text-xs text-zinc-500 mt-0.5">{standing.description}</div>
            </div>
            <div className="flex items-center gap-2" data-testid="standing-marks">
              {[0, 1, 2].map((i) => {
                const isGreen = i < standing.greens;
                return (
                  <div key={i} className={`w-10 h-10 border flex items-center justify-center font-bold text-xl ${isGreen ? "border-emerald-500 text-emerald-400 bg-emerald-500/10" : "border-red-500 text-red-400 bg-red-500/10"}`}>
                    {isGreen ? "✓" : "✕"}
                  </div>
                );
              })}
            </div>
          </div>
          {standing.history?.length > 0 && (
            <div className="mt-4 pt-4 border-t border-white/5 space-y-2" data-testid="standing-history">
              <div className="font-mono text-[10px] uppercase tracking-widest text-red-400">Active strike{standing.history.length === 1 ? "" : "s"}</div>
              {standing.history.map((s) => (
                <div key={s.id} className="text-xs text-zinc-400 border-l-2 border-red-500/50 pl-3">
                  <div className="text-red-300">{s.reason}</div>
                  <div className="text-zinc-500">{new Date(s.issued_at).toLocaleString()}</div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Vendor status card / upgrade card follow below */}
      {!isVendor ? (
        <section
          className="mt-6 h57-surface p-6 border-[#FFD700]/40"
          data-testid="vendor-upgrade-card"
        >
          <div className="flex items-start gap-3">
            <LockKey size={22} weight="bold" className="text-[#FFD700] shrink-0 mt-1" />
            <div className="flex-1">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFD700]">→ Become a Vendor</div>
              <h2 className="mt-1 font-display font-black uppercase text-xl tracking-tight">Sell Cards on Hunt57</h2>
              <p className="mt-3 text-sm text-zinc-400 leading-relaxed">
                Two ways in: apply for review by our team, or fast-track with a referral code from a current
                Tier 3 Hunt57 vendor. New vendors start at <span className="text-white font-semibold">Tier 1</span> —
                up to 25 active listings and a $50 maximum list price. Complete qualifying sales to unlock Tier 2 and Tier 3.
              </p>

              {applicationPending && (
                <div className="mt-4 p-3 border border-[#FFD700]/40 bg-[#FFD700]/5 text-sm text-zinc-300" data-testid="application-pending">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFD700]">→ Application Pending</div>
                  <p className="mt-1">
                    Your vendor application is in the queue. An admin will review it shortly.
                    You can keep browsing while you wait.
                  </p>
                </div>
              )}
              {applicationRejected && (
                <div className="mt-4 p-3 border border-red-500/40 bg-red-500/5 text-sm text-red-300" data-testid="application-rejected">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-red-400">→ Application Not Approved</div>
                  <p className="mt-1">
                    Your application was declined{user.vendor_application_rejection_reason ? `: ${user.vendor_application_rejection_reason}` : "."}
                    You can re-apply below or use a referral code.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Universal prerequisite — payment method required for BOTH paths.
              Extracted out of Option 1 so users understand it's a shared step. */}
          {billing && !billing.is_billing_exempt && (
            <div
              className={`mt-5 p-4 border ${billing.has_payment_method ? "border-emerald-500/40 bg-emerald-500/5" : "border-[#FFD700]/50 bg-[#FFD700]/5"}`}
              data-testid="cc-prereq-card"
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-start gap-2">
                  <CreditCard
                    size={18}
                    weight="bold"
                    className={`mt-0.5 ${billing.has_payment_method ? "text-emerald-400" : "text-[#FFD700]"}`}
                  />
                  <div>
                    <div className={`font-mono text-[10px] uppercase tracking-widest ${billing.has_payment_method ? "text-emerald-400" : "text-[#FFD700]"}`}>
                      → Step 1 — Payment Method {billing.has_payment_method ? "· Ready" : "· Required"}
                    </div>
                    <p className="mt-1 text-xs text-zinc-300 leading-relaxed">
                      {billing.has_payment_method ? (
                        <>Card on file. You can now apply <span className="text-white">or</span> redeem a referral code below.</>
                      ) : (
                        <>Required for <span className="text-white">both</span> Option 1 (Apply) and Option 2 (Referral Code). <span className="text-white">You won&apos;t be charged for 30 days after approval</span>, then <span className="line-through text-zinc-500">$69.99</span> <span className="text-[#FFD700]">$49.99</span> first year. Cancel anytime.</>
                      )}
                    </p>
                  </div>
                </div>
                {!billing.has_payment_method ? (
                  <button
                    onClick={startCardSetup}
                    disabled={ccBusy}
                    data-testid="add-cc-btn"
                    className="h57-btn-primary text-xs whitespace-nowrap"
                  >
                    {ccBusy ? "…" : "Add Card →"}
                  </button>
                ) : (
                  <button
                    onClick={openBillingPortal}
                    disabled={ccBusy}
                    data-testid="update-cc-btn"
                    className="h57-btn-secondary text-xs whitespace-nowrap"
                  >
                    {ccBusy ? "…" : "Update Card"}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Application path */}
          <div className="mt-5 p-4 border border-white/10">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Option 1 — Standard Application</div>
            <p className="mt-1 text-sm text-zinc-400">Answer a few questions. Admin reviews and approves. <span className="text-white">Your 30-day free trial starts on approval.</span></p>
            {!applyOpen ? (
              <button
                type="button"
                onClick={() => setApplyOpen(true)}
                disabled={applicationPending || (billing && !billing.is_billing_exempt && !billing.has_payment_method)}
                data-testid="apply-vendor-btn"
                className="h57-btn-secondary mt-3 text-xs"
              >
                {applicationPending
                  ? "Application Pending Review"
                  : (billing && !billing.is_billing_exempt && !billing.has_payment_method)
                    ? "Add card first"
                    : "Apply to Become a Vendor →"}
              </button>
            ) : (
              <div className="mt-3 space-y-3">
                <div>
                  <label className="h57-label">Why do you want to sell on Hunt57?</label>
                  <textarea className="h57-input min-h-[70px]" data-testid="apply-reason"
                    value={application.reason} maxLength={1000}
                    onChange={(e) => setApplication({ ...application, reason: e.target.value })}
                    placeholder="Tell us about the collection you plan to list…" />
                </div>
                <div>
                  <label className="h57-label">Selling experience (optional)</label>
                  <input className="h57-input" data-testid="apply-experience"
                    value={application.experience} maxLength={500}
                    onChange={(e) => setApplication({ ...application, experience: e.target.value })}
                    placeholder="eBay ID, Whatnot handle, LCS relationship, etc." />
                </div>
                <div>
                  <label className="h57-label">Social / Instagram (optional)</label>
                  <input className="h57-input" data-testid="apply-social"
                    value={application.social} maxLength={200}
                    onChange={(e) => setApplication({ ...application, social: e.target.value })}
                    placeholder="@handle" />
                </div>
                <LegalNotice
                  variant="vendor"
                  checked={acceptVendorTerms}
                  onChange={setAcceptVendorTerms}
                  testId="apply-vendor-legal"
                />
                <div className="flex gap-2 justify-end">
                  <button type="button" onClick={() => setApplyOpen(false)} className="h57-btn-secondary text-xs">Cancel</button>
                  <button type="button" onClick={submitApplication} disabled={applying || !acceptVendorTerms} data-testid="apply-submit"
                    className="h57-btn-primary text-xs">
                    {applying ? "Submitting…" : "Submit Application"}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Referral path */}
          <div className="mt-4 p-4 border border-white/10">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Option 2 — Referral Fast-Track</div>
            <p className="mt-1 text-sm text-zinc-400">Got a code from a Tier 3 vendor? Skip the queue. <span className="text-white">Same 30-day free trial applies.</span></p>
            {billing && !billing.is_billing_exempt && !billing.has_payment_method && (
              <p className="mt-2 text-xs text-[#FFD700]" data-testid="voucher-cc-hint">
                Add a payment method in Step 1 above to enable code redemption.
              </p>
            )}
            <form onSubmit={upgradeToVendor} className="mt-3 flex flex-col gap-3">
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  value={vendorCode}
                  onChange={(e) => setVendorCode(e.target.value.toUpperCase())}
                  placeholder="HUNTV-XXXXXX or HUNT57-XXXXXX"
                  data-testid="vendor-code-input"
                  className="h57-input font-mono uppercase tracking-widest flex-1"
                  autoComplete="off"
                  spellCheck={false}
                />
                <button
                  type="submit"
                  disabled={upgrading || !acceptVendorTerms || (billing && !billing.is_billing_exempt && !billing.has_payment_method)}
                  data-testid="vendor-upgrade-btn"
                  className="h57-btn-primary inline-flex items-center gap-2 justify-center whitespace-nowrap"
                  title={(billing && !billing.is_billing_exempt && !billing.has_payment_method) ? "Add a payment method first" : ""}
                >
                  {upgrading
                    ? "Upgrading…"
                    : (billing && !billing.is_billing_exempt && !billing.has_payment_method)
                      ? "Add card first"
                      : (<>Redeem Code <PawPrint size={16} weight="fill" /></>)}
                </button>
              </div>
              <LegalNotice
                variant="vendor"
                checked={acceptVendorTerms}
                onChange={setAcceptVendorTerms}
                testId="upgrade-vendor-legal"
              />
            </form>
          </div>
        </section>
      ) : (
        <section className="mt-6 h57-surface p-6 border-[#FF5F1F]/30" data-testid="vendor-status-card">
          {!tierStatus ? (
            <div className="text-center py-6 font-mono text-xs uppercase tracking-widest text-zinc-500">
              Loading tier…
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 flex-wrap">
                <PawPrint size={22} weight="fill" className="text-[#FF5F1F]" />
                <div className="flex-1">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF5F1F]">→ Vendor Account · Active</div>
                  <div className="mt-1 font-display font-bold uppercase tracking-tight">
                    {tierMeta?.label || `Tier ${tier}`}
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5">{tierMeta?.desc}</div>
                </div>
                <Trophy size={20} weight="fill" className={tier === 3 ? "text-[#FFD700]" : tier === 2 ? "text-[#FF5F1F]" : "text-zinc-500"} />
              </div>

          {tierStatus && (
            <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
              <div className="p-3 border border-white/10">
                <div className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest">Active Listings</div>
                <div className="font-display font-black text-xl mt-0.5" data-testid="tier-active-count">
                  {tierStatus.active_listings_count}
                  {tierStatus.max_active_listings ? <span className="text-zinc-500 text-sm">/{tierStatus.max_active_listings}</span> : ""}
                </div>
              </div>
              <div className="p-3 border border-white/10">
                <div className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest">Qualifying Sales</div>
                <div className="font-display font-black text-xl mt-0.5" data-testid="tier-sales-count">{tierStatus.qualifying_sales_count}</div>
              </div>
              <div className="p-3 border border-white/10">
                <div className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest">Max List Price</div>
                <div className="font-display font-black text-xl mt-0.5" data-testid="tier-max-price">
                  {tierStatus.max_price != null ? `$${tierStatus.max_price.toFixed(0)}` : "None"}
                </div>
              </div>
            </div>
          )}

          {tierStatus?.next_threshold && (
            <div className="mt-3 p-3 border border-[#FFD700]/30 bg-[#FFD700]/5">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFD700]">
                → Next: Tier {tierStatus.next_threshold.tier}
              </div>
              <div className="mt-1 text-sm text-zinc-300">
                {Math.max(0, tierStatus.next_threshold.sales_needed - tierStatus.qualifying_sales_count)} more qualifying
                sale{tierStatus.next_threshold.sales_needed - tierStatus.qualifying_sales_count === 1 ? "" : "s"} unlocks Tier {tierStatus.next_threshold.tier}.
              </div>
              <div className="mt-2 h-2 bg-white/5 relative overflow-hidden" data-testid="tier-progress-bar">
                <div
                  className="h-full bg-[#FF5F1F] transition-all"
                  style={{
                    width: `${Math.min(100, (tierStatus.qualifying_sales_count / tierStatus.next_threshold.sales_needed) * 100)}%`,
                  }}
                />
              </div>
            </div>
          )}

          <div className="mt-4 text-xs text-zinc-500 font-mono uppercase tracking-widest">
            {user.founding_member ? (
              <>Founding Member — lifetime free membership.</>
            ) : user.vendor_since ? (
              <>Vendor since {new Date(user.vendor_since).toLocaleDateString()} · Membership: {user.membership_status || "trial"}</>
            ) : (
              <>Membership: {user.membership_status || "trial"}</>
            )}
          </div>

          {/* Billing sub-card — shows trial countdown + Manage Billing button */}
          {billing && !billing.is_billing_exempt && (
            <div className="mt-4 pt-4 border-t border-white/5" data-testid="billing-card">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFD700]">→ Billing</div>
                  <div className="mt-1 font-display font-bold uppercase tracking-tight text-sm">
                    {billing.membership_status === "trial" && "30-day free trial"}
                    {billing.membership_status === "active" && `Active · $${((billing.vendor_price_cents || 6999) / 100).toFixed(2)}/year`}
                    {billing.membership_status === "past_due" && "⚠ Payment past due"}
                    {billing.membership_status === "cancelled" && "Cancelled"}
                    {!["trial", "active", "past_due", "cancelled"].includes(billing.membership_status) && (billing.membership_status || "—")}
                  </div>
                  {billing.current_period_end && (
                    <div className="mt-0.5 text-xs text-zinc-500">
                      {billing.cancel_at_period_end ? "Ends" : billing.membership_status === "trial" ? "Trial ends / next charge" : "Renews"}{" "}
                      {new Date(billing.current_period_end * 1000).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                    </div>
                  )}
                  {billing.cancel_at_period_end && (
                    <div className="mt-1 h57-chip !border-red-500/50 !text-red-400 !py-1 !px-2 text-[10px]">Cancels at period end</div>
                  )}
                </div>
                <button onClick={openBillingPortal} disabled={ccBusy} data-testid="manage-billing-btn"
                  className="h57-btn-secondary text-xs inline-flex items-center gap-1">
                  <CreditCard size={12} weight="bold" />
                  {ccBusy ? "…" : billing.stripe_subscription_id ? "Manage Billing" : "Update Card"}
                </button>
              </div>
              {billing.membership_status === "trial" && billing.current_period_end && !billing.cancel_at_period_end && (
                <TrialCountdown
                  trialEndUnix={billing.current_period_end}
                  trialDays={billing.trial_days || 30}
                  priceLabel={`$${((billing.vendor_first_year_price_cents ?? billing.vendor_price_cents ?? 4999) / 100).toFixed(2)}`}
                />
              )}
            </div>
          )}

          {billing?.is_billing_exempt && (
            <div className="mt-4 pt-4 border-t border-white/5 flex items-center gap-2 text-xs text-zinc-500" data-testid="billing-exempt-badge">
              <ShieldCheck size={14} weight="bold" className="text-[#FFD700]" />
              <span>Billing-exempt — {user.role === "admin" ? "Admin" : "Founding Member"}. Never charged.</span>
            </div>
          )}
            </>
          )}
        </section>
      )}

      {/* Voucher management — Tier 3 only */}
      {isVendor && canCreateVouchers && (
        <section className="mt-6 h57-surface p-6 border-[#FFD700]/30" data-testid="vouchers-section">
          <div className="flex items-center gap-3">
            <Ticket size={22} weight="fill" className="text-[#FFD700]" />
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFD700]">→ Referral Vouchers · Tier 3</div>
              <div className="mt-1 font-display font-bold uppercase tracking-tight">Grow the Marketplace</div>
              <p className="mt-1 text-xs text-zinc-500">
                Generate voucher codes to fast-track trusted friends into vendor status. Each redemption is tied back to you.
              </p>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-2">
            <input
              type="number" min="1" max="100"
              className="h57-input md:col-span-1"
              placeholder="Max uses"
              data-testid="voucher-max-uses"
              value={newVoucher.max_uses}
              onChange={(e) => setNewVoucher({ ...newVoucher, max_uses: e.target.value })}
            />
            <input
              className="h57-input md:col-span-2"
              placeholder="Note (optional — who's this for?)"
              data-testid="voucher-note"
              value={newVoucher.note}
              onChange={(e) => setNewVoucher({ ...newVoucher, note: e.target.value })}
            />
            <button
              type="button"
              onClick={createVoucher}
              disabled={creatingVoucher}
              data-testid="voucher-create-btn"
              className="h57-btn-primary text-sm inline-flex items-center gap-2 justify-center"
            >
              {creatingVoucher ? "…" : (<>Create Voucher <Ticket size={14} weight="bold" /></>)}
            </button>
          </div>

          {vouchers.length > 0 && (
            <div className="mt-4 space-y-2" data-testid="voucher-list">
              {vouchers.map((v) => (
                <div key={v.id} className="flex items-center gap-3 border border-white/10 p-3 flex-wrap" data-testid={`voucher-row-${v.id}`}>
                  <button
                    onClick={() => copy(v.code)}
                    className="font-mono text-sm tracking-widest text-white hover:text-[#FFD700] inline-flex items-center gap-1"
                    title="Copy"
                    data-testid={`voucher-copy-${v.id}`}
                  >
                    {v.code} <Copy size={11} weight="bold" />
                  </button>
                  <span className="text-xs text-zinc-500 font-mono">
                    {(v.used_by?.length || 0)}/{v.max_uses} used
                  </span>
                  <span className={`h57-chip !py-1 !px-2 ${v.status === "revoked" ? "!border-red-500/50 !text-red-400" : ""}`}>
                    {v.status}
                  </span>
                  {v.note && <span className="text-xs text-zinc-400 flex-1 truncate">{v.note}</span>}
                  {v.status === "active" && (
                    <button onClick={() => revokeVoucher(v.id, v.code)} data-testid={`voucher-revoke-${v.id}`}
                      className="text-xs text-red-400 hover:text-red-300">Revoke</button>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {isVendor && tierStatus && !canCreateVouchers && tier < 3 && (
        <section className="mt-6 h57-surface p-4 border-white/10 opacity-70">
          <div className="flex items-start gap-3">
            <Ticket size={18} weight="regular" className="text-zinc-500 mt-1" />
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Referral Vouchers · Locked</div>
              <p className="mt-1 text-xs text-zinc-500">
                Voucher generation unlocks at Tier 3 (25 qualifying sales).
              </p>
            </div>
          </div>
        </section>
      )}

      <form onSubmit={save} className="mt-8 space-y-8">
        {/* Profile */}
        <section className="space-y-4">
          <h2 className="font-display font-bold uppercase text-sm tracking-widest text-zinc-400">Profile</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="h57-label">Display Name</label>
              <input className="h57-input" data-testid="account-display-name" value={form.display_name || ""} onChange={(e) => setForm({ ...form, display_name: e.target.value })} />
            </div>
            <div>
              <label className="h57-label">State / Region</label>
              <input className="h57-input" data-testid="account-state" value={form.state || ""} onChange={(e) => setForm({ ...form, state: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="h57-label">Bio</label>
            <textarea className="h57-input min-h-[80px]" data-testid="account-bio" value={form.bio || ""} onChange={(e) => setForm({ ...form, bio: e.target.value })} placeholder="Rookies, autos, vintage — what you hunt." />
          </div>
        </section>

        {/* Shipping Address — visible to ALL users. Buyers need this so sellers
            know where to ship after they mark a PO paid. Kept PRIVATE — never
            shown on public profile; only surfaced to the seller on their
            mark-paid email + PO card. */}
        <section className="space-y-4" data-testid="shipping-address-section">
          <div>
            <h2 className="font-display font-bold uppercase text-sm tracking-widest text-zinc-400">
              Shipping Address <span className="text-[10px] font-mono text-zinc-500 normal-case tracking-wider">(where sellers should ship your cards)</span>
            </h2>
            <p className="mt-1 text-[11px] text-zinc-500 leading-relaxed">
              This address is <span className="text-white">private</span> and never appears on your public profile. It is shared with a seller only after you mark one of their POs as paid — they need it to ship your card.
            </p>
          </div>
          <div>
            <label className="h57-label">Recipient Name</label>
            <input
              className="h57-input"
              data-testid="account-shipping-name"
              value={form.shipping_name || ""}
              onChange={(e) => setForm({ ...form, shipping_name: e.target.value })}
              placeholder="Full name for the package label"
              maxLength={120}
            />
          </div>
          <div>
            <label className="h57-label">Address Line 1</label>
            <input
              className="h57-input"
              data-testid="account-shipping-line1"
              value={form.shipping_line1 || ""}
              onChange={(e) => setForm({ ...form, shipping_line1: e.target.value })}
              placeholder="Street address"
              maxLength={200}
            />
          </div>
          <div>
            <label className="h57-label">Address Line 2 <span className="text-[10px] text-zinc-500 normal-case tracking-normal">(optional)</span></label>
            <input
              className="h57-input"
              data-testid="account-shipping-line2"
              value={form.shipping_line2 || ""}
              onChange={(e) => setForm({ ...form, shipping_line2: e.target.value })}
              placeholder="Apt / Suite / PO Box"
              maxLength={200}
            />
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="h57-label">City</label>
              <input
                className="h57-input"
                data-testid="account-shipping-city"
                value={form.shipping_city || ""}
                onChange={(e) => setForm({ ...form, shipping_city: e.target.value })}
                maxLength={100}
              />
            </div>
            <div>
              <label className="h57-label">State / Region</label>
              <input
                className="h57-input"
                data-testid="account-shipping-state"
                value={form.shipping_state || ""}
                onChange={(e) => setForm({ ...form, shipping_state: e.target.value })}
                placeholder="TX"
                maxLength={60}
              />
            </div>
            <div>
              <label className="h57-label">ZIP / Postal Code</label>
              <input
                className="h57-input"
                data-testid="account-shipping-zip"
                value={form.shipping_zip || ""}
                onChange={(e) => setForm({ ...form, shipping_zip: e.target.value })}
                maxLength={20}
              />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="h57-label">Country</label>
              <input
                className="h57-input"
                data-testid="account-shipping-country"
                value={form.shipping_country || ""}
                onChange={(e) => setForm({ ...form, shipping_country: e.target.value })}
                placeholder="US"
                maxLength={60}
              />
            </div>
            <div>
              <label className="h57-label">Phone <span className="text-[10px] text-zinc-500 normal-case tracking-normal">(optional · sometimes required by carriers)</span></label>
              <input
                className="h57-input"
                data-testid="account-shipping-phone"
                value={form.shipping_phone || ""}
                onChange={(e) => setForm({ ...form, shipping_phone: e.target.value })}
                placeholder="+1 555-0100"
                maxLength={40}
              />
            </div>
          </div>
        </section>

        {/* Payment paths — only surface for vendors, but keep form field bindings so nothing breaks */}
        {isVendor && (
          <>
            <section className="space-y-4">
              <h2 className="font-display font-bold uppercase text-sm tracking-widest text-zinc-400">Payment Paths <span className="text-[10px] font-mono text-zinc-500 normal-case tracking-wider">(how buyers pay you)</span></h2>
              <p className="text-xs text-zinc-500">Add at least one to start selling. Buyers send payment <span className="text-white">directly to you</span> via these — Hunt57 never touches sale funds. <span className="text-zinc-400">This is separate from your subscription card above.</span></p>
              <div>
                <label className="h57-label">PayPal Link</label>
                <input className="h57-input" data-testid="account-paypal" value={form.paypal_link || ""} onChange={(e) => setForm({ ...form, paypal_link: e.target.value })} placeholder="https://paypal.me/yourname" />
                {(() => {
                  const v = (form.paypal_link || "").trim();
                  if (!v) return null;
                  const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
                  if (isEmail) {
                    return (
                      <p className="mt-1.5 text-[11px] leading-snug text-[#FFD700]/90 flex items-start gap-1.5" data-testid="account-paypal-email-warning">
                        <WarningCircle size={12} weight="bold" className="mt-0.5 shrink-0" />
                        <span>
                          Buyers get one-click checkout when you use a <span className="text-white font-mono">paypal.me/handle</span> link. Emails still work but require the buyer to open PayPal and paste the address manually. Set up a free handle at <a href="https://paypal.me" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">paypal.me</a>.
                        </span>
                      </p>
                    );
                  }
                  return null;
                })()}
              </div>
              <div>
                <label className="h57-label">Cash App Handle</label>
                <input className="h57-input" data-testid="account-cashapp" value={form.cashapp_handle || ""} onChange={(e) => setForm({ ...form, cashapp_handle: e.target.value })} placeholder="$yourhandle" />
              </div>
              <div>
                <label className="h57-label">Venmo Link (optional)</label>
                <input className="h57-input" data-testid="account-venmo" value={form.venmo_link || ""} onChange={(e) => setForm({ ...form, venmo_link: e.target.value })} placeholder="https://venmo.com/u/yourname" />
              </div>
              <div>
                <label className="h57-label">Other Payment Notes</label>
                <input className="h57-input" data-testid="account-other-payment" value={form.other_payment_notes || ""} onChange={(e) => setForm({ ...form, other_payment_notes: e.target.value })} placeholder="Zelle by request, F&F only, etc." />
              </div>
              {/* Credit-card acceptance — the seller supplies a per-transaction pay link at validation. */}
              <div className="border border-white/10 p-3 space-y-2">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-1"
                    data-testid="account-accepts-cc"
                    checked={!!form.accepts_credit_card}
                    onChange={(e) => {
                      if (e.target.checked) {
                        // Don't flip the state yet — show the confirmation modal.
                        // If the user confirms, we flip to true. If they cancel,
                        // the toggle remains off.
                        setCcConfirmOpen(true);
                      } else {
                        setForm({ ...form, accepts_credit_card: false });
                      }
                    }}
                  />
                  <div>
                    <div className="text-sm text-white">Accept credit card payments</div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">
                      You&apos;ll paste a unique pay-link (Stripe / Square / PayPal invoice) for each sale at validation time.
                    </div>
                  </div>
                </label>
                {form.accepts_credit_card && (
                  <input
                    className="h57-input text-sm"
                    data-testid="account-cc-note"
                    value={form.credit_card_note || ""}
                    onChange={(e) => setForm({ ...form, credit_card_note: e.target.value })}
                    placeholder="Buyer-visible note (e.g. 'Stripe link · 3% surcharge applies')"
                    maxLength={200}
                  />
                )}
              </div>
              <PaymentDisclaimer compact />
            </section>

            {/* Shipping */}
            <section className="space-y-4">
              <h2 className="font-display font-bold uppercase text-sm tracking-widest text-zinc-400">Shipping</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="h57-label">Ships From</label>
                  <input className="h57-input" data-testid="account-ships-from" value={form.ships_from_state || ""} onChange={(e) => setForm({ ...form, ships_from_state: e.target.value })} placeholder="TX" />
                </div>
                <div>
                  <label className="h57-label">Typical Ship Time</label>
                  <input className="h57-input" data-testid="account-ship-time" value={form.typical_ship_time || ""} onChange={(e) => setForm({ ...form, typical_ship_time: e.target.value })} placeholder="1-2 business days" />
                </div>
              </div>
              <div>
                <label className="h57-label">Shipping Policy</label>
                <textarea className="h57-input min-h-[80px]" data-testid="account-ship-policy" value={form.shipping_policy || ""} onChange={(e) => setForm({ ...form, shipping_policy: e.target.value })} />
              </div>
              <div>
                <label className="h57-label">Return Policy</label>
                <textarea className="h57-input min-h-[80px]" data-testid="account-return-policy" value={form.return_policy || ""} onChange={(e) => setForm({ ...form, return_policy: e.target.value })} />
              </div>

              {/* Order handling time — how many days after PO validation the
                  vendor promises to ship. Missing this triggers an auto strike. */}
              <div className="border-t border-white/10 pt-6" data-testid="handling-time-section">
                <label className="h57-label">Order Handling Time</label>
                <div className="flex flex-wrap items-center gap-3 mt-1">
                  <input
                    type="number"
                    step="1"
                    min="1"
                    max="7"
                    className="h57-input !w-24"
                    data-testid="handling-time-input"
                    value={form.handling_time_days ?? 3}
                    onChange={(e) => setForm({ ...form, handling_time_days: e.target.value })}
                  />
                  <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">Days (max 7)</span>
                </div>
                <p className="mt-2 text-xs text-zinc-500 leading-relaxed">
                  You commit to shipping within this window after a Purchase Order is validated. Missing it — without marking the order as shipped (with a tracking number if available) — will result in an automatic account strike. Repeat offenses can lead to suspension.
                </p>
              </div>

              {/* Combined shipping policy — displayed on every listing to
                  eliminate back-and-forth on multi-card deals. */}
              <div className="border-t border-white/10 pt-6" data-testid="combined-shipping-section">
                <div className="flex items-center justify-between">
                  <label className="h57-label !mb-0">Combined Shipping</label>
                  <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={!!form.combined_shipping_enabled}
                      onChange={(e) => setForm({ ...form, combined_shipping_enabled: e.target.checked })}
                      data-testid="combined-shipping-toggle"
                      className="w-4 h-4 accent-[#FF5F1F]"
                    />
                    <span className="font-mono text-[10px] tracking-widest uppercase text-zinc-400">
                      {form.combined_shipping_enabled ? "Enabled" : "Disabled"}
                    </span>
                  </label>
                </div>
                <p className="mt-1 text-xs text-zinc-500">Let buyers save on shipping when they purchase multiple cards from you in the same package.</p>

                {form.combined_shipping_enabled && (
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="h57-label">Initial Charge ($)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="999.99"
                        className="h57-input"
                        data-testid="combined-shipping-first"
                        value={form.combined_shipping_first}
                        onChange={(e) => setForm({ ...form, combined_shipping_first: e.target.value })}
                        placeholder="5.99"
                      />
                      <div className="mt-1 font-mono text-[9px] uppercase tracking-widest text-zinc-500">First card in the package</div>
                    </div>
                    <div>
                      <label className="h57-label">Per Additional Card ($)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="999.99"
                        className="h57-input"
                        data-testid="combined-shipping-addl"
                        value={form.combined_shipping_addl}
                        onChange={(e) => setForm({ ...form, combined_shipping_addl: e.target.value })}
                        placeholder="0.25"
                      />
                      <div className="mt-1 font-mono text-[9px] uppercase tracking-widest text-zinc-500">Each extra card in same package</div>
                    </div>
                    <div>
                      <label className="h57-label">Max Cards / Package</label>
                      <input
                        type="number"
                        step="1"
                        min="2"
                        max="500"
                        className="h57-input"
                        data-testid="combined-shipping-max"
                        value={form.combined_shipping_max}
                        onChange={(e) => setForm({ ...form, combined_shipping_max: e.target.value })}
                        placeholder="20"
                      />
                      <div className="mt-1 font-mono text-[9px] uppercase tracking-widest text-zinc-500">Full charge starts over past this cap</div>
                    </div>
                  </div>
                )}
              </div>
            </section>
          </>
        )}

        <button type="submit" disabled={saving} className="h57-btn-primary w-full" data-testid="account-save">
          {saving ? "Saving…" : "Save Profile"}
        </button>
      </form>

      {/* Confirmation modal: Accept Credit Card Payments
          Reminds the vendor that Hunt57 does NOT process CC — they must
          have their own way to send a unique pay link per sale. */}
      <AlertDialog open={ccConfirmOpen} onOpenChange={setCcConfirmOpen}>
        <AlertDialogContent
          data-testid="cc-accept-confirm-modal"
          className="!bg-[#121212] border border-white/15 text-zinc-200 shadow-2xl"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase tracking-tight">
              Accept credit card payments?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-zinc-300 leading-relaxed">
                <p>
                  <span className="text-white font-semibold">Hunt57 does not process credit card payments.</span>{" "}
                  We only list the option on your card&apos;s buy page. To actually collect CC payment, you
                  must have your own way to generate a unique pay link for each sale.
                </p>
                <div className="border border-[#FFD700]/30 bg-[#FFD700]/5 p-3">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFD700] mb-1">You&apos;ll need one of:</div>
                  <ul className="list-disc pl-5 space-y-1 text-xs text-zinc-300">
                    <li><span className="text-white">PayPal Business account</span> — send invoice or generate payment link</li>
                    <li><span className="text-white">Stripe account</span> — create a Payment Link per sale</li>
                    <li><span className="text-white">Square</span> — Square Invoice / Checkout link</li>
                    <li>Any other processor that lets you create a one-off checkout URL</li>
                  </ul>
                </div>
                <p className="text-[13px] text-zinc-400">
                  When a buyer picks Credit Card, you&apos;ll paste your pay-link at PO validation time.
                  <span className="text-[#FFD700]"> If you cannot provide a working pay-link, the buyer will not be able to pay you</span> and the sale may be cancelled or lead to a strike.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cc-accept-confirm-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="cc-accept-confirm-ok"
              onClick={() => setForm((prev) => ({ ...prev, accepts_credit_card: true }))}
            >
              Confirm — I have a way to send a pay link
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AccountSecuritySection />

    </div>
  );
}


function AccountSecuritySection() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [cp, setCp] = useState({ current: "", next: "", confirm: "" });
  const [cpBusy, setCpBusy] = useState(false);
  const [close, setClose] = useState({ password: "", confirm_username: "", ack: false });
  const [closeBusy, setCloseBusy] = useState(false);

  const doChangePw = async (e) => {
    e.preventDefault();
    if (cp.next.length < 6) { toast.error("New password must be at least 6 characters."); return; }
    if (cp.next !== cp.confirm) { toast.error("New passwords don\u2019t match."); return; }
    setCpBusy(true);
    try {
      await api.post("/users/me/change-password", { current_password: cp.current, new_password: cp.next });
      toast.success("Password updated.");
      setCp({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setCpBusy(false);
    }
  };

  const doCloseAccount = async () => {
    if (!close.ack) { toast.error("Please acknowledge the account-close consequences."); return; }
    if (!window.confirm("This will PERMANENTLY close your Hunt57 account and remove your active listings. Continue?")) return;
    setCloseBusy(true);
    try {
      await api.post("/users/me/close-account", { password: close.password, confirm_username: close.confirm_username });
      toast.success("Account closed. You've been signed out.");
      await logout?.();
      navigate("/");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setCloseBusy(false);
    }
  };

  return (
    <div className="mt-10 space-y-6" data-testid="account-security">
      <section className="h57-surface p-5 space-y-3" data-testid="account-change-password">
        <h3 className="font-display font-bold text-sm uppercase tracking-widest">Change Password</h3>
        <form onSubmit={doChangePw} className="space-y-3">
          <div>
            <label className="h57-label">Current password</label>
            <input type="password" required className="h57-input" value={cp.current} onChange={(e) => setCp({ ...cp, current: e.target.value })} data-testid="account-cp-current" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="h57-label">New password</label>
              <input type="password" required minLength={6} className="h57-input" value={cp.next} onChange={(e) => setCp({ ...cp, next: e.target.value })} data-testid="account-cp-new" />
            </div>
            <div>
              <label className="h57-label">Confirm new</label>
              <input type="password" required minLength={6} className="h57-input" value={cp.confirm} onChange={(e) => setCp({ ...cp, confirm: e.target.value })} data-testid="account-cp-confirm" />
            </div>
          </div>
          <button type="submit" disabled={cpBusy} className="h57-btn-primary" data-testid="account-cp-submit">
            {cpBusy ? "Updating…" : "Update password"}
          </button>
        </form>
      </section>

      <section className="h57-surface p-5 space-y-3 border border-red-500/30" data-testid="account-close-account">
        <h3 className="font-display font-bold text-sm uppercase tracking-widest text-red-400">Close Account</h3>
        <p className="text-xs text-zinc-400 leading-snug">
          Closing your account is permanent. Your active listings will be removed. Your past transactions and reviews remain visible to counterparties for their records. You will not be able to sign back in with this account.
        </p>
        <div>
          <label className="h57-label">Your current password</label>
          <input type="password" className="h57-input" value={close.password} onChange={(e) => setClose({ ...close, password: e.target.value })} data-testid="account-close-password" />
        </div>
        <div>
          <label className="h57-label">Type your username <span className="font-mono text-white">{user?.username}</span> to confirm</label>
          <input className="h57-input font-mono" value={close.confirm_username} onChange={(e) => setClose({ ...close, confirm_username: e.target.value })} data-testid="account-close-username" placeholder={user?.username} />
        </div>
        <label className="flex items-start gap-2 text-xs text-zinc-400">
          <input type="checkbox" checked={close.ack} onChange={(e) => setClose({ ...close, ack: e.target.checked })} data-testid="account-close-ack" className="mt-0.5 accent-red-500" />
          <span>I understand this will close my account, deactivate my active listings, and cannot be undone from within the app.</span>
        </label>
        <button
          type="button"
          onClick={doCloseAccount}
          disabled={closeBusy || !close.ack || !close.password || close.confirm_username.trim().toLowerCase() !== (user?.username || "").toLowerCase()}
          data-testid="account-close-submit"
          className="h57-btn-primary !bg-red-500 !border-red-500 hover:!bg-red-600 disabled:opacity-40"
        >
          {closeBusy ? "Closing…" : "Close my account"}
        </button>
      </section>
    </div>
  );
}
